import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Download, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const XIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

export default function ShareStatsButton({ disabled }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);

  const create = async () => {
    setLoading(true); setOpen(true);
    try { const { data } = await api.post("/share/stats"); setShare(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setOpen(false); }
    finally { setLoading(false); }
  };

  const tweet = () => {
    if (!share) return;
    const s = share.stats;
    const pnl = s.totalPnl >= 0 ? `+$${Math.abs(s.totalPnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `-$${Math.abs(s.totalPnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    const text = `My CatalystCaddy stats: ${pnl} P&L · ${s.winRate != null ? `${Math.round(s.winRate)}% win rate` : `${s.trades} trades`}${s.bestTrade ? ` · best trade $${s.bestTrade.ticker} ${s.bestTrade.roiPct > 0 ? "+" : ""}${Math.round(s.bestTrade.roiPct)}%` : ""}\n\nScan before you ape 👇`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(share.url)}`, "_blank", "noopener");
  };
  const copy = async () => { await navigator.clipboard.writeText(share.url); toast.success("Link copied"); };
  const download = () => { const a = document.createElement("a"); a.href = share.imageUrl; a.download = `catalystcaddy-stats-${share.id}.png`; a.target = "_blank"; a.click(); };

  return (
    <>
      <Button size="sm" onClick={create} disabled={disabled} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="share-stats-btn">
        <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-[#334566] max-w-2xl" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Your stats card</DialogTitle>
            <DialogDescription className="text-slate-400">Post it to X — the link unfurls into this card. Snapshot taken just now.</DialogDescription>
          </DialogHeader>
          {loading || !share ? (
            <div className="aspect-[1200/630] rounded-xl bg-[#111723] border border-[#222F43] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>
          ) : (
            <img src={share.imageUrl} alt="CatalystCaddy stats card" className="w-full rounded-xl border border-[#222F43]" data-testid="share-card-img" />
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={tweet} disabled={!share} className="bg-white hover:bg-slate-200 text-black font-bold flex-1" data-testid="share-x-btn"><XIcon className="h-4 w-4 mr-2" /> Share to X</Button>
            <Button onClick={download} disabled={!share} variant="outline" className="border-[#334566] bg-transparent" data-testid="share-download-btn"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
            <Button onClick={copy} disabled={!share} variant="outline" className="border-[#334566] bg-transparent" data-testid="share-copy-btn"><Link2 className="h-4 w-4 mr-1.5" /> Copy link</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
